import { useState } from 'react';
import { api, ApiCallError, setSession } from '../api/client';
import type { Parent } from '@xiaopeiban/shared';

type Tab = 'login' | 'register';
type LoginStep = 'email' | 'code';
type RegisterStep = 'form' | 'code';

export default function LoginPage({ onLogin }: { onLogin: (p: Parent) => void }) {
  const [tab, setTab] = useState<Tab>('login');

  // login
  const [loginStep, setLoginStep] = useState<LoginStep>('email');
  const [loginMode, setLoginMode] = useState<'magic' | 'password'>('password');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);

  // register
  const [regStep, setRegStep] = useState<RegisterStep>('form');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regCode, setRegCode] = useState('');
  const [regDevCode, setRegDevCode] = useState<string | null>(null);

  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function clearStatus() { setErr(null); }

  async function requestCode() {
    clearStatus();
    if (!email.trim()) { setErr('请填邮箱'); return; }
    setLoading(true);
    try {
      const r = await api.requestLoginCode(email.trim().toLowerCase());
      if (r.devCode) setDevCode(r.devCode);
      setLoginStep('code');
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function submitCode() {
    clearStatus();
    if (!/^\d{6}$/.test(code)) { setErr('请输入 6 位验证码'); return; }
    setLoading(true);
    try {
      const r = await api.verifyLoginCode(email.trim().toLowerCase(), code);
      setSession(r.token, 'parent');
      const me = await api.getMe();
      onLogin(me.parent);
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function submitPassword() {
    clearStatus();
    if (!password) { setErr('请填密码'); return; }
    setLoading(true);
    try {
      const r = await api.loginWithPassword(email.trim().toLowerCase(), password);
      setSession(r.token, 'parent');
      const me = await api.getMe();
      onLogin(me.parent);
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function submitRegisterStart() {
    clearStatus();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim())) { setErr('邮箱格式不对'); return; }
    if (regPassword.length < 6) { setErr('密码至少 6 位'); return; }
    setLoading(true);
    try {
      const r = await api.parentRegisterStart(regEmail.trim().toLowerCase(), regPassword);
      if (r.devCode) setRegDevCode(r.devCode);
      setRegStep('code');
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  async function submitRegisterComplete() {
    clearStatus();
    if (!/^\d{6}$/.test(regCode)) { setErr('请输入 6 位验证码'); return; }
    setLoading(true);
    try {
      const r = await api.parentRegisterComplete(regEmail.trim().toLowerCase(), regCode);
      setSession(r.token, 'parent');
      const me = await api.getMe();
      onLogin(me.parent);
    } catch (e) { setErr(niceError(e)); } finally { setLoading(false); }
  }

  return (
    <div className="page" style={{ maxWidth: 420 }}>
      <div style={{ textAlign: 'center', marginTop: 40, marginBottom: 24 }}>
        <div style={{ fontSize: 48 }}>🐹</div>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginTop: 8 }}>小陪伴 · 家长后台</h1>
        <div className="muted" style={{ marginTop: 4 }}>注册账号，给孩子创建小陪伴吧</div>
      </div>

      <div className="flex" style={{ marginBottom: 16, gap: 8 }}>
        <button
          className={`btn ${tab === 'login' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ flex: 1 }}
          onClick={() => { setTab('login'); clearStatus(); }}
        >
          登录
        </button>
        <button
          className={`btn ${tab === 'register' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ flex: 1 }}
          onClick={() => { setTab('register'); clearStatus(); }}
        >
          新家长注册
        </button>
      </div>

      <div className="card">
        {tab === 'login' && loginStep === 'email' && (
          <>
            <div className="flex" style={{ marginBottom: 16, gap: 8 }}>
              <button
                className={`btn ${loginMode === 'password' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ flex: 1 }}
                onClick={() => setLoginMode('password')}
              >
                密码登录
              </button>
              <button
                className={`btn ${loginMode === 'magic' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ flex: 1 }}
                onClick={() => setLoginMode('magic')}
              >
                邮箱验证码
              </button>
            </div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>邮箱</label>
            <input
              type="email"
              autoComplete="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (loginMode === 'magic' ? requestCode() : submitPassword())}
            />
            {loginMode === 'password' && (
              <>
                <label style={{ display: 'block', fontWeight: 700, margin: '12px 0 6px' }}>密码</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submitPassword()}
                />
              </>
            )}
            {err && <div className="error-box">{err}</div>}
            <button
              className="btn btn-primary btn-block"
              style={{ marginTop: 16 }}
              disabled={loading}
              onClick={loginMode === 'magic' ? requestCode : submitPassword}
            >
              {loading ? <span className="spinner" /> : loginMode === 'magic' ? '发送验证码' : '登录'}
            </button>
          </>
        )}

        {tab === 'login' && loginStep === 'code' && (
          <>
            <div className="muted" style={{ marginBottom: 12 }}>
              验证码已发到 <b>{email}</b>。15 分钟内有效。
            </div>
            {devCode && (
              <div className="success-box">
                <b>开发模式：</b> 验证码 <code style={{ fontSize: 18 }}>{devCode}</code>
              </div>
            )}
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>6 位验证码</label>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && submitCode()}
              style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8 }}
            />
            {err && <div className="error-box">{err}</div>}
            <div className="flex" style={{ marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setLoginStep('email')} disabled={loading}>
                返回
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={submitCode} disabled={loading}>
                {loading ? <span className="spinner" /> : '验证并登录'}
              </button>
            </div>
          </>
        )}

        {tab === 'register' && regStep === 'form' && (
          <>
            <div className="muted" style={{ marginBottom: 12, lineHeight: 1.6 }}>
              先注册家长账号，登录后再为孩子创建账号（每个家长最多 2 个孩子）
            </div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>家长邮箱</label>
            <input
              type="email"
              autoComplete="email"
              placeholder="your@email.com"
              value={regEmail}
              onChange={e => setRegEmail(e.target.value)}
            />
            <label style={{ display: 'block', fontWeight: 700, margin: '12px 0 6px' }}>设置密码（至少 6 位）</label>
            <input
              type="password"
              autoComplete="new-password"
              value={regPassword}
              onChange={e => setRegPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitRegisterStart()}
            />
            {err && <div className="error-box">{err}</div>}
            <button
              className="btn btn-primary btn-block"
              style={{ marginTop: 16 }}
              disabled={loading}
              onClick={submitRegisterStart}
            >
              {loading ? <span className="spinner" /> : '发送验证码 →'}
            </button>
          </>
        )}

        {tab === 'register' && regStep === 'code' && (
          <>
            <div className="muted" style={{ marginBottom: 12 }}>
              验证码已发到 <b>{regEmail}</b>，输入后即可激活账号。
            </div>
            {regDevCode && (
              <div className="success-box">
                <b>开发模式：</b> 验证码 <code style={{ fontSize: 18 }}>{regDevCode}</code>
              </div>
            )}
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>6 位验证码</label>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={regCode}
              onChange={e => setRegCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && submitRegisterComplete()}
              style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8 }}
            />
            {err && <div className="error-box">{err}</div>}
            <div className="flex" style={{ marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setRegStep('form')} disabled={loading}>
                返回
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={submitRegisterComplete} disabled={loading}>
                {loading ? <span className="spinner" /> : '激活并进入 🎉'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function niceError(e: unknown): string {
  if (e instanceof ApiCallError) return e.message;
  return (e as Error)?.message || '出错了，稍后再试';
}
